import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "..";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { getSearchQuery } from "../common/helpers";
import { IQueryParams } from "../common/common.types";
import { IMaterialRequest } from "./MaterialRequest.types";
import { IHandleDynamicApproval } from "../expenseModule";
import {
  getFileNameFromUrl,
  uploadMultipleFile,
} from "../../components/FileUpload/utils";
import { isArray } from "lodash";
import { getUniqueId } from "../../helpers";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";

//=============================== Material Request List ===============================
export const FETCH_MATERIAL_REQUEST_LIST_PROGRESS =
  "FETCH_MATERIAL_REQUEST_LIST_PROGRESS";
export const FETCH_MATERIAL_REQUEST_LIST_SUCCESS =
  "FETCH_MATERIAL_REQUEST_LIST_SUCCESS";
export const FETCH_MATERIAL_REQUEST_LIST_FAILED =
  "FETCH_MATERIAL_REQUEST_LIST_FAILED";

export const fetchMaterialRequestListProgress = () =>
  action(FETCH_MATERIAL_REQUEST_LIST_PROGRESS);
export const fetchMaterialRequestListSuccess = (
  list: IMaterialRequest[],
  totalRecords: number,
) => action(FETCH_MATERIAL_REQUEST_LIST_SUCCESS, { list, totalRecords });
export const fetchMaterialRequestListFailed = () =>
  action(FETCH_MATERIAL_REQUEST_LIST_FAILED);

export const fetchMaterialRequestListAsync =
  (
    queryParams: IQueryParams,
    type: "ALL" | "APPROVAL",
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const searchQuery = getSearchQuery(queryParams);
      dispatch(fetchMaterialRequestListProgress());
      let url = `/grn/get-material-request${searchQuery}`;
      if (type === "APPROVAL") {
        url = `/dynamicApproval/get-approval${searchQuery}&table_name=latest_material_request`;
      }
      const res = await api.get(url);
      const data: IMaterialRequest[] = res.data.data ?? [];
      const totalRecords = res.data.totalRecords ?? 0;

      dispatch(fetchMaterialRequestListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchMaterialRequestListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err?.response?.data?.message || "Unknown Error Occurred",
          displayAs: "snackbar",
        }),
      );
    }
  };

//=============================== Fetch Single Material Request ===============================
export const FETCH_SINGLE_MATERIAL_REQUEST_PROGRESS =
  "FETCH_SINGLE_MATERIAL_REQUEST_PROGRESS";
export const FETCH_SINGLE_MATERIAL_REQUEST_SUCCESS =
  "FETCH_SINGLE_MATERIAL_REQUEST_SUCCESS";
export const FETCH_SINGLE_MATERIAL_REQUEST_FAILED =
  "FETCH_SINGLE_MATERIAL_REQUEST_FAILED";

export const fetchSingleMaterialRequestProgress = () =>
  action(FETCH_SINGLE_MATERIAL_REQUEST_PROGRESS);
export const fetchSingleMaterialRequestSuccess = (data: IMaterialRequest) =>
  action(FETCH_SINGLE_MATERIAL_REQUEST_SUCCESS, { data });
export const fetchSingleMaterialRequestFailed = (errorMessage: string) =>
  action(FETCH_SINGLE_MATERIAL_REQUEST_FAILED, { errorMessage });

export const fetchSingleMaterialRequestAsync =
  (
    uuid: string,
    billing_company_uuid?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchSingleMaterialRequestProgress());
      let url = `/grn/get-material-request?material_request_uuid=${uuid}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }
      const res = await api.get(url);
      const data = res.data.data ?? [];
      if (data.length > 0) {
        let mrData = data[0];
        mrData.attachments = isArray(mrData.attachments)
          ? mrData.attachments
              .filter((item: any) => typeof item === "string")
              .map((file: string) => {
                return {
                  key: getUniqueId(),
                  type: "SERVER",
                  file: null,
                  name: getFileNameFromUrl(file || ""),
                  path: file,
                } as IFileUpload;
              })
          : [];
        dispatch(fetchSingleMaterialRequestSuccess(data[0]));
      } else {
        dispatch(
          fetchSingleMaterialRequestFailed(
            "Oops! We couldn't find any records.",
          ),
        );
      }
    } catch (err: any) {
      dispatch(fetchSingleMaterialRequestFailed("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err?.response?.data?.message ?? "Unknown Error Occurred",
          displayAs: "snackbar",
        }),
      );
    }
  };

//=============================== Upsert Single Material Request ===============================
export const upsertSingleMaterialRequestAsync =
  (
    payload: IMaterialRequest,
    onCallback: (isSuccess: boolean, data?: IMaterialRequest) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());

      let {
        create_ts,
        insert_ts,
        combined_material_request_no,
        created_by_name,
        modified_by_name,
        created_by_uuid,
        modified_by_uuid,
        running_no,
        financial_year,
        rowId,
        prefix,
        special_approval_uuids,

        ...restPayload
      } = payload;

      const asPayload = {
        title: restPayload.material_request_date,
      };

      // Corrected logic for mapping attachments
      const fileToUpload = isArray(restPayload.attachments)
        ? restPayload.attachments.filter((item) => item.type === "LOCAL")
        : [];
      if (fileToUpload.length > 0) {
        const { paths } = await uploadMultipleFile(
          fileToUpload,
          "MATERIAL_REQUEST",
          asPayload,
        );
        restPayload.attachments = restPayload.attachments
          .filter((item) => item.type === "SERVER" && item.path)
          .map((item) => item.path!)
          .concat(paths as string[]) as any;
      } else {
        restPayload.attachments = restPayload.attachments
          .filter((item) => item.type === "SERVER" && item.path)
          .map((item) => item.path!) as any;
      }

      const res = await api.post("/grn/upsert-material-request", restPayload);

      const responseData: IMaterialRequest = res.data.data;

      onCallback(true, responseData);
      dispatch(
        showMessage({
          type: "success",
          message: "Material Request saved successfully!",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err?.response?.data?.message ?? "Unknown Error Occurred",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

//=============================== Clear State ===============================
export const CLEAR_SINGLE_MATERIAL_REQUEST = "CLEAR_SINGLE_MATERIAL_REQUEST";
export const CLEAR_MATERIAL_REQUEST_STATE = "CLEAR_MATERIAL_REQUEST_STATE";
export const clearSingleMaterialRequestSync = () =>
  action(CLEAR_SINGLE_MATERIAL_REQUEST);
export const clearMaterialRequestStateSync = () =>
  action(CLEAR_MATERIAL_REQUEST_STATE);

// ********************** Handle MR Approval ********************** //
export interface IHandleMRDynamicApproval {
  dynamic_approval_uuid: string;
  material_request_uuid: string;
  remark: string;
  fields?: any;
  status: "APPROVED" | "REJECTED" | "ROLLBACK";
}

export const handleMrDynamicApprovalAsync =
  (
    data: IHandleMRDynamicApproval,
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/grn/approve-material-request", data);
      let message = "Action success!";
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: message,
        }),
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(true);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
